// Which weekdays nobody is working, so the calendar can grey them out.
//
// The practice does not work weekends today, but that is a property of the
// providers' schedules rather than a fact about Saturdays: a provider who
// starts working Saturdays should simply open that column. Hiding the weekend
// outright made the calendar disagree with the Providers tab and gave staff no
// way to see the shape of the week.

export interface ProviderSchedule {
  id: string;
  name?: string;
  /** Day names as stored on the provider, e.g. ['monday', 'tuesday']. */
  workingDays?: string[];
}

// Date.getDay() order: Sunday is 0.
const WEEKDAY_NUMBERS: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * The weekdays no ticked provider works, as Date.getDay() numbers.
 *
 * Returns an empty set when there is nobody to reason about — no providers
 * loaded, or none ticked. Unticking every provider already empties the
 * calendar; grewing out the whole week on top of that reads as an outage
 * rather than a filter.
 */
export const nonWorkingWeekdays = (
  providers: ProviderSchedule[],
  selectedProviderIds: string[]
): Set<number> => {
  const selected = providers.filter((p) => selectedProviderIds.includes(p.id));
  if (selected.length === 0) return new Set();

  const worked = new Set<number>();
  selected.forEach((provider) => {
    (provider.workingDays || []).forEach((day) => {
      const weekday = WEEKDAY_NUMBERS[String(day).trim().toLowerCase()];
      if (weekday !== undefined) worked.add(weekday);
    });
  });

  // A provider with no schedule on record tells us nothing, so if that is all
  // we have, leave the week alone rather than closing every day.
  if (worked.size === 0) return new Set();

  return new Set([0, 1, 2, 3, 4, 5, 6].filter((day) => !worked.has(day)));
};

/** FullCalendar's day classes, in Date.getDay() order. */
export const WEEKDAY_CLASS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
